import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, Check, Loader2, Shield, FileText, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface CreateBotWizardProps {
  tenantId: string;
  tenantIndustry: string | null;
  onComplete: () => void;
}

interface Template {
  id: string;
  industry: string;
  purpose: string;
  default_governance_pack_id: string | null;
  default_trust_charter: string | null;
  default_widget_settings: any;
  default_escalation_format: any;
}

interface GovernancePack {
  id: string;
  industry: string;
  version: number | null;
  public_trust_charter: string | null;
  internal_rules: any;
}

interface SourceBundle {
  id: string;
  name: string;
  industry: string;
  status: string | null;
}

interface TenantData {
  summary_forward_email?: string | null;
  developer_email?: string | null;
  specialisations?: any;
  do_not_mention?: string | null;
}

const TONE_OPTIONS = [
  { value: 'conservative', label: 'Conservative', description: 'Formal, cautious language' },
  { value: 'neutral', label: 'Neutral', description: 'Balanced, professional tone' },
  { value: 'friendly', label: 'Friendly', description: 'Approachable, warm communication' },
];

const SPECIALISATION_OPTIONS = [
  'Tax Advisory',
  'Business Advisory',
  'Farm & Rural',
  'Trust & Estates',
  'Property & Construction',
  'Healthcare',
  'Hospitality',
  'Retail & E-commerce',
  'Manufacturing',
  'Professional Services',
];

const CreateBotWizard: React.FC<CreateBotWizardProps> = ({ 
  tenantId, 
  tenantIndustry,
  onComplete 
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Data from database
  const [templates, setTemplates] = useState<Template[]>([]);
  const [governancePacks, setGovernancePacks] = useState<GovernancePack[]>([]);
  const [sourceBundles, setSourceBundles] = useState<SourceBundle[]>([]);
  const [tenantData, setTenantData] = useState<TenantData | null>(null);
  const [approvedAssetsCount, setApprovedAssetsCount] = useState(0);

  // Form state - Purpose Template
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedGovernancePack, setSelectedGovernancePack] = useState<GovernancePack | null>(null);

  // Form state - Source Bundle
  const [selectedSourceBundleId, setSelectedSourceBundleId] = useState<string>('');

  // Form state - Nuance Pack
  const [specialisations, setSpecialisations] = useState<string[]>([]);
  const [specialisationNotes, setSpecialisationNotes] = useState('');
  const [positioningLine, setPositioningLine] = useState('');
  const [doNotMention, setDoNotMention] = useState('');
  const [toneModifier, setToneModifier] = useState<string>('neutral');

  // Form state - Destinations
  const [summaryForwardEmail, setSummaryForwardEmail] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [emailSummaryEnabled, setEmailSummaryEnabled] = useState(true);
  const [newsletterOptinEnabled, setNewsletterOptinEnabled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch templates filtered by industry
        const industry = tenantIndustry || 'accounting';
        
        const [templatesRes, governanceRes, bundlesRes, tenantRes, advisoryTenantRes, assetsRes] = await Promise.all([
          supabase.from('templates').select('*').eq('industry', industry),
          supabase.from('governance_packs').select('*'),
          supabase.from('source_bundles').select('*').eq('industry', industry),
          supabase.from('tenants').select('developer_email').eq('id', tenantId).single(),
          supabase.from('advisory_tenants').select('summary_forward_email, developer_email, specialisations, do_not_mention').eq('id', tenantId).maybeSingle(),
          supabase.from('tenant_assets')
            .select('id')
            .eq('tenant_id', tenantId)
            .eq('ingestion_status', 'approved'),
        ]);

        if (templatesRes.data) setTemplates(templatesRes.data);
        if (governanceRes.data) setGovernancePacks(governanceRes.data);
        if (bundlesRes.data) {
          setSourceBundles(bundlesRes.data);
          // Preselect first available bundle
          if (bundlesRes.data.length > 0) {
            setSelectedSourceBundleId(bundlesRes.data[0].id);
          }
        }
        
        // Try advisory_tenants first (has extended fields), fallback to tenants
        const tenantInfo = advisoryTenantRes.data || tenantRes.data;
        if (tenantInfo) {
          const data: TenantData = {
            summary_forward_email: (tenantInfo as any).summary_forward_email || null,
            developer_email: tenantInfo.developer_email || null,
            specialisations: (tenantInfo as any).specialisations || null,
            do_not_mention: (tenantInfo as any).do_not_mention || null,
          };
          setTenantData(data);
          
          // Pre-fill from tenant data
          if (data.summary_forward_email) {
            setSummaryForwardEmail(data.summary_forward_email);
          }
          if (data.do_not_mention) {
            setDoNotMention(data.do_not_mention);
          }
          if (data.specialisations?.selected) {
            setSpecialisations(data.specialisations.selected);
          }
          if (data.specialisations?.notes) {
            setSpecialisationNotes(data.specialisations.notes);
          }
        }
        if (assetsRes.data) {
          setApprovedAssetsCount(assetsRes.data.length);
        }
      } catch (error) {
        console.error('Error fetching wizard data:', error);
        toast.error('Failed to load configuration data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId, tenantIndustry]);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    
    if (template) {
      setSelectedTemplate(template);
      
      // Auto-apply governance pack from template
      if (template.default_governance_pack_id) {
        const pack = governancePacks.find(g => g.id === template.default_governance_pack_id);
        if (pack) setSelectedGovernancePack(pack);
      }
    }
  };

  const toggleSpecialisation = (spec: string) => {
    setSpecialisations(prev => 
      prev.includes(spec) 
        ? prev.filter(s => s !== spec)
        : [...prev, spec]
    );
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a purpose template');
      return;
    }

    if (!summaryForwardEmail) {
      toast.error('Summary forward email is required');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from('bots').insert({
        tenant_id: tenantId,
        template_id: selectedTemplate.id,
        governance_pack_id: selectedGovernancePack?.id || null,
        source_bundle_id: selectedSourceBundleId && selectedSourceBundleId !== 'none' ? selectedSourceBundleId : null,
        environment: 'preview',
        status: 'draft',
        mode: 'client_only',
        nuance_pack: {
          specialisations: specialisations,
          specialisation_notes: specialisationNotes.slice(0, 180),
          positioning_line: positioningLine.slice(0, 180),
          do_not_mention: doNotMention.slice(0, 500),
          tone_modifier: toneModifier,
        },
        widget_settings: selectedTemplate.default_widget_settings,
        email_summary_enabled: emailSummaryEnabled,
        summary_forward_email: summaryForwardEmail || null,
        webhook_url: webhookUrl || null,
        newsletter_optin_enabled: newsletterOptinEnabled,
      });

      if (error) throw error;
      
      toast.success('Preview bot created successfully');
      onComplete();
    } catch (error) {
      console.error('Error creating bot:', error);
      toast.error('Failed to create bot');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = selectedTemplate && summaryForwardEmail;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <ScrollArea className="h-[70vh] pr-4">
      <div className="space-y-6 pb-6">
        {/* Section 1: Purpose Template (Required) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              Purpose Template
            </CardTitle>
            <CardDescription>
              Select a template matching this tenant's industry. This determines governance rules and default settings.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="template">Template *</Label>
              <Select value={selectedTemplateId} onValueChange={handleTemplateSelect}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a purpose template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <span className="capitalize">{template.purpose.replace(/_/g, ' ')}</span>
                      <span className="text-muted-foreground ml-2">({template.industry})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  No templates available for industry "{tenantIndustry}". Create one first.
                </p>
              )}
            </div>

            {/* Auto-applied Governance Preview */}
            {selectedGovernancePack && (
              <div className="border rounded-lg p-4 bg-muted/50">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-primary" />
                  <p className="font-medium text-sm">Auto-Applied Governance Pack</p>
                  <Badge variant="secondary" className="ml-auto">v{selectedGovernancePack.version || 1}</Badge>
                </div>
                
                {selectedGovernancePack.public_trust_charter && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">Public Trust Charter</p>
                    <p className="text-sm bg-background p-2 rounded border">
                      {selectedGovernancePack.public_trust_charter}
                    </p>
                  </div>
                )}

                {selectedGovernancePack.internal_rules && (
                  <div className="grid grid-cols-2 gap-3">
                    {selectedGovernancePack.internal_rules.forbidden_topics && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Forbidden Topics</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedGovernancePack.internal_rules.forbidden_topics.slice(0, 5).map((topic: string, i: number) => (
                            <Badge key={i} variant="destructive" className="text-xs">{topic}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedGovernancePack.internal_rules.escalation_triggers && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Escalation Triggers</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedGovernancePack.internal_rules.escalation_triggers.slice(0, 5).map((trigger: string, i: number) => (
                            <Badge key={i} variant="outline" className="text-xs">{trigger}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-3 italic">
                  Governance rules cannot be edited in v1. Contact admin for changes.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 2: Source Bundle */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Source Bundle
            </CardTitle>
            <CardDescription>
              Attach indexed knowledge sources. The bot will also use {approvedAssetsCount} approved tenant assets.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="sourceBundle">Knowledge Bundle</Label>
              <Select value={selectedSourceBundleId} onValueChange={setSelectedSourceBundleId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a source bundle..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No bundle (tenant assets only)</SelectItem>
                  {sourceBundles.map((bundle) => (
                    <SelectItem key={bundle.id} value={bundle.id}>
                      {bundle.name}
                      <Badge variant="outline" className="ml-2 text-xs">{bundle.status}</Badge>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {approvedAssetsCount > 0 && (
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <Check className="w-4 h-4" />
                {approvedAssetsCount} approved tenant asset{approvedAssetsCount !== 1 ? 's' : ''} will be included in the knowledge pack.
              </p>
            )}
            {approvedAssetsCount === 0 && (
              <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                No approved tenant assets. Review the Assets tab to approve items.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Section 3: Nuance Pack */}
        <Card>
          <CardHeader>
            <CardTitle>Nuance Pack</CardTitle>
            <CardDescription>
              Customize the bot's personality and boundaries. These don't override governance rules.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Specialisations */}
            <div>
              <Label>Specialisations</Label>
              <p className="text-xs text-muted-foreground mb-2">Select areas this bot should emphasize</p>
              <div className="flex flex-wrap gap-2">
                {SPECIALISATION_OPTIONS.map((spec) => (
                  <Badge
                    key={spec}
                    variant={specialisations.includes(spec) ? 'default' : 'outline'}
                    className="cursor-pointer hover:bg-primary/80"
                    onClick={() => toggleSpecialisation(spec)}
                  >
                    {spec}
                  </Badge>
                ))}
              </div>
              <Input
                className="mt-2"
                value={specialisationNotes}
                onChange={(e) => setSpecialisationNotes(e.target.value)}
                placeholder="Additional specialisation notes (optional, max 180 chars)"
                maxLength={180}
              />
              <p className="text-xs text-muted-foreground text-right">{specialisationNotes.length}/180</p>
            </div>

            <Separator />

            {/* Positioning Line */}
            <div>
              <Label htmlFor="positioningLine">Approved Positioning Line</Label>
              <p className="text-xs text-muted-foreground mb-1">How the bot should describe the firm</p>
              <Input
                id="positioningLine"
                value={positioningLine}
                onChange={(e) => setPositioningLine(e.target.value)}
                placeholder="e.g., 'We're a boutique accounting firm specialising in...'"
                maxLength={180}
              />
              <p className="text-xs text-muted-foreground text-right">{positioningLine.length}/180</p>
            </div>

            {/* Do Not Mention */}
            <div>
              <Label htmlFor="doNotMention">Do Not Mention</Label>
              <p className="text-xs text-muted-foreground mb-1">Topics or competitors the bot should avoid</p>
              <Textarea
                id="doNotMention"
                value={doNotMention}
                onChange={(e) => setDoNotMention(e.target.value)}
                placeholder="e.g., 'Never mention competitor X or service Y'"
                maxLength={500}
                rows={3}
              />
              <p className="text-xs text-muted-foreground text-right">{doNotMention.length}/500</p>
            </div>

            {/* Tone Modifier */}
            <div>
              <Label>Tone Modifier</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {TONE_OPTIONS.map((tone) => (
                  <button
                    key={tone.value}
                    type="button"
                    onClick={() => setToneModifier(tone.value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      toneModifier === tone.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <p className="font-medium text-sm">{tone.label}</p>
                    <p className="text-xs text-muted-foreground">{tone.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Destinations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Destinations
            </CardTitle>
            <CardDescription>
              Configure where conversation summaries and leads are sent.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="summaryEmail">Summary Forward Email *</Label>
              <Input
                id="summaryEmail"
                type="email"
                value={summaryForwardEmail}
                onChange={(e) => setSummaryForwardEmail(e.target.value)}
                placeholder="team@company.com"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Required. Conversation summaries will be sent here.
              </p>
            </div>

            <div>
              <Label htmlFor="developerEmail">Developer/IT Email</Label>
              <Input
                id="developerEmail"
                value={tenantData?.developer_email || ''}
                disabled
                className="mt-1 bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Read from tenant settings. Edit in tenant overview if needed.
              </p>
            </div>

            <div>
              <Label htmlFor="webhookUrl">Webhook URL (Optional)</Label>
              <Input
                id="webhookUrl"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://..."
                className="mt-1"
              />
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="emailSummary"
                  checked={emailSummaryEnabled}
                  onCheckedChange={(checked) => setEmailSummaryEnabled(!!checked)}
                />
                <Label htmlFor="emailSummary" className="font-normal">
                  Enable email summaries after each conversation
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="newsletterOptin"
                  checked={newsletterOptinEnabled}
                  onCheckedChange={(checked) => setNewsletterOptinEnabled(!!checked)}
                />
                <Label htmlFor="newsletterOptin" className="font-normal">
                  Enable newsletter opt-in prompt for visitors
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Section */}
        <Card className="border-primary">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Environment</span>
                <Badge variant="secondary" className="bg-purple-500/10 text-purple-500">Preview</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Initial Status</span>
                <Badge variant="secondary" className="bg-gray-500/10 text-gray-500">Draft</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Mode</span>
                <Badge variant="outline">Client Only</Badge>
              </div>
              <Separator />
              <p className="text-xs text-muted-foreground">
                After creation, the bot will need to pass QA before it can be published. Chat execution is not enabled in draft status.
              </p>
            </div>
          </CardContent>
        </Card>

        <Button 
          onClick={handleSubmit} 
          disabled={!canSubmit || submitting}
          className="w-full"
          size="lg"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Check className="w-4 h-4 mr-2" />
          )}
          Create Preview Bot
        </Button>
      </div>
    </ScrollArea>
  );
};

export default CreateBotWizard;
